/**
 * DELIVERY MODAL
 *
 * Creates or edits a delivery work item linked to a parent job.
 * Comparable to AppointmentModal but focused on delivery-type children.
 *
 * @date February 2026
 */

'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X, Plus, Truck, Calendar, Clock, MapPin, User, Loader2, Package, Scale, Factory, Building2, Info,
  Search, ChevronDown, Check, AlertTriangle, Briefcase, Mail, Phone, Users, Target,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import { emitOperationsGuidedTourTrainingEvent } from '@/lib/operations/guidedTourEvents';
import { useOperationsAssignmentOptions } from '@/hooks/useOperationsAssignmentOptions';
import { useWorkItemsData, type WorkItem } from '@/hooks/useWorkItemsData';
import { extractCoordinates, getContactLocationCoordinates } from '@/lib/work-items/appointmentLocationUtils';
import { fetchContactPeople as fetchGuardedContactPeople } from '@/lib/contacts/fetchContactPeople';
import QuickLocationForm from '@/components/work-items/QuickLocationForm';
import type { CreatedLocation } from '@/components/work-items/QuickLocationForm';
import LocationAutocomplete, { type CustomLocationData } from '@/components/maps/LocationAutocomplete';
import UnifiedContactLookup, {
  type UnifiedLookupContactPerson,
  type UnifiedLookupLocation,
} from '@/components/shared/UnifiedContactLookup';

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

const CONTACT_LOOKUP_REQUEST_TIMEOUT_MS = 15_000;
const CONTACT_LOCATION_LOAD_ERROR_MESSAGE = 'We could not load locations for this contact right now.';
const CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE = 'Loading locations took too long. Please retry or use a custom location.';

type DeliveryCustomFields = Record<string, unknown> & {
  require_exact_location?: boolean;
  require_location_verification?: boolean;
  geofence_radius_meters?: number;
  additional_notes?: string;
  equipment_required?: string;
  safety_requirements?: string;
};

interface Contact {
  id: number;
  company_name: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  contact_type: string;
  account_status?: string | null;
  is_active?: boolean | null;
}

interface ContactPerson {
  id: number;
  contact_id: number;
  first_name: string;
  last_name: string;
  job_title: string | null;
  email: string | null;
  phone: string | null;
}

interface JobSearchResult {
  id: string;
  item_number: string;
  title: string;
  description?: string | null;
  notes?: string | null;
  status: string;
  module_context?: string | null;
  contact_id?: number | null;
  contact_name: string | null;
  contact_person?: string | null;
  contact_person_id?: number | null;
  location_id: number | null;
  location_name: string | null;
  location_address: string | null;
  location_coordinates?: string | null;
  custom_fields?: DeliveryCustomFields;
}

interface DeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  parentJob?: {
    id: string;
    item_number?: string;
    title?: string;
    description?: string | null;
    module_context?: string | null;
    contact_id?: number | null;
    contact_name?: string | null;
    location_id?: number | null;
    location_name?: string | null;
    location_address?: string | null;
    location_coordinates?: string | null;
    custom_fields?: Record<string, unknown> | null;
  } | null;
  editingDelivery?: {
    id: string;
    title: string;
    description?: string | null;
    status: string;
    priority?: string | null;
    item_subtype?: string | null;
    scheduled_start?: string | null;
    scheduled_end?: string | null;
    assigned_to?: number | number[] | null;
    location_id?: number | null;
    location_name?: string | null;
    location_address?: string | null;
    location_coordinates?: string | null;
    module_context?: string | null;
    custom_fields?: Record<string, unknown> | null;
  } | null;
  activeSolutionKey?: string | null;
}

interface ContactLocation {
  id: number;
  contact_id: number;
  location_name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  is_primary?: boolean;
  latitude?: number | null;
  longitude?: number | null;
  coordinates?: unknown;
}

type DeliveryLocationMode = 'inherited' | 'contact' | 'custom';

// ============================================================================
// STATUS CONFIG
// ============================================================================

const DELIVERY_STATUSES = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'en_route', label: 'En Route' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'arrived', label: 'Arrived' },
  { value: 'collected', label: 'Collected' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'failed', label: 'Failed' },
  { value: 'missed', label: 'Missed' },
];

type DeliverySubtypeOption = {
  value: string;
  label: string;
  description: string;
};

const LOGISTICS_DELIVERY_SUBTYPES: DeliverySubtypeOption[] = [
  { value: 'delivery', label: 'Delivery', description: 'Standard outbound or inbound delivery stop' },
  { value: 'pickup', label: 'Pickup', description: 'Pickup or collection stop' },
  { value: 'return', label: 'Return', description: 'Return or reverse-logistics stop' },
];

const WASTE_DELIVERY_SUBTYPES: DeliverySubtypeOption[] = [
  { value: 'waste_collection', label: 'Waste Collection', description: 'Collection stop requiring chain-of-custody paperwork' },
  { value: 'waste_disposal', label: 'Waste Disposal', description: 'Disposal run to a processing or disposal site' },
  { value: 'waste_origin', label: 'Waste Origin Transfer', description: 'Origin capture or transfer-point movement' },
];

function normalizeModuleContext(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function normalizeStringValue(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

function isWasteModuleContext(value: unknown): boolean {
  return normalizeModuleContext(value).includes('waste');
}

function inferSubtypeOptions(moduleContext: unknown): DeliverySubtypeOption[] {
  return isWasteModuleContext(moduleContext)
    ? [...LOGISTICS_DELIVERY_SUBTYPES, ...WASTE_DELIVERY_SUBTYPES]
    : LOGISTICS_DELIVERY_SUBTYPES;
}

function defaultSubtypeForModuleContext(moduleContext: unknown): string {
  return isWasteModuleContext(moduleContext) ? 'waste_collection' : 'delivery';
}

function formatSubtypeLabel(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePositiveIntegerId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

function buildLocationDisplayLabel(location?: ContactLocation | null): string {
  if (!location) {
    return '';
  }

  return [
    location.location_name,
    location.address_line1,
    location.address_line2,
    location.city,
    location.state,
    location.postal_code,
  ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0).join(', ');
}

function matchesParentLocation(location: ContactLocation, parentJob?: DeliveryModalProps['parentJob']): boolean {
  if (!parentJob) {
    return false;
  }

  const parentLocationId = normalizePositiveIntegerId(parentJob.location_id);
  if (parentLocationId && Number(location.id) === parentLocationId) {
    return true;
  }

  return location.location_name === parentJob.location_name && buildLocationDisplayLabel(location) === parentJob.location_address;
}

function isEditingInheritedLocation(editingDelivery: NonNullable<DeliveryModalProps['editingDelivery']>, parentJob?: DeliveryModalProps['parentJob']): boolean {
  if (!parentJob) {
    return false;
  }

  const sameLocationId = normalizePositiveIntegerId(editingDelivery.location_id) != null
    && normalizePositiveIntegerId(parentJob.location_id) != null
    && normalizePositiveIntegerId(editingDelivery.location_id) === normalizePositiveIntegerId(parentJob.location_id);

  if (sameLocationId) {
    return true;
  }

  return normalizeStringValue(editingDelivery.location_name) === normalizeStringValue(parentJob.location_name)
    && normalizeStringValue(editingDelivery.location_address) === normalizeStringValue(parentJob.location_address);
}

function parseDeliveryCustomFields(value: unknown): DeliveryCustomFields | undefined {
  if (!value) return undefined;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as DeliveryCustomFields;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as DeliveryCustomFields;
      }
    } catch {
      return undefined;
    }
  }

  return undefined;
}

function buildLocationLoadErrorMessage(error: unknown): string {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE;
  }

  if (error instanceof Error && /abort|timeout/i.test(error.message)) {
    return CONTACT_LOCATION_LOAD_TIMEOUT_MESSAGE;
  }

  return CONTACT_LOCATION_LOAD_ERROR_MESSAGE;
}

async function requestContactLocations(contactId: number, token: string): Promise<{ locations: ContactLocation[]; errorMessage: string | null }> {
  if (!token) {
    return {
      locations: [],
      errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
    };
  }

  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), CONTACT_LOOKUP_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abortController.signal,
    });

    if (!response.ok) {
      return {
        locations: [],
        errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
      };
    }

    const payload = await response.json().catch(() => null) as { success?: boolean; data?: ContactLocation[] } | null;
    if (payload?.success === false) {
      return {
        locations: [],
        errorMessage: CONTACT_LOCATION_LOAD_ERROR_MESSAGE,
      };
    }

    return {
      locations: Array.isArray(payload?.data) ? payload.data : [],
      errorMessage: null,
    };
  } catch (error) {
    return {
      locations: [],
      errorMessage: buildLocationLoadErrorMessage(error),
    };
  } finally {
    clearTimeout(timeoutId);
  }
}

function resolveSelectedLocationFromList(nextLocations: ContactLocation[], previousLocation: ContactLocation | null): ContactLocation | null {
  if (previousLocation?.id != null) {
    const matched = nextLocations.find((location) => Number(location.id) === Number(previousLocation.id));
    if (matched) {
      return matched;
    }
  }

  const primaryWithCoordinates = nextLocations.find((location) => location.is_primary && location.latitude && location.longitude);
  return primaryWithCoordinates || null;
}

function buildSyntheticLocationFromJob(job: JobSearchResult): ContactLocation | null {
  const contactId = normalizePositiveIntegerId(job.contact_id);
  const locationId = normalizePositiveIntegerId(job.location_id);

  if (!contactId || !locationId) {
    return null;
  }

  const coordinates = extractCoordinates(job.location_coordinates);

  return {
    id: locationId,
    contact_id: contactId,
    location_name: job.location_name || 'Job location',
    address_line1: job.location_address || null,
    city: null,
    is_primary: false,
    latitude: coordinates?.lat ?? null,
    longitude: coordinates?.lng ?? null,
    coordinates: coordinates ?? job.location_coordinates ?? null,
  };
}

function buildCustomLocationFromJob(job: JobSearchResult): CustomLocationData | null {
  if (normalizePositiveIntegerId(job.location_id) || !job.location_name) {
    return null;
  }

  const coordinates = extractCoordinates(job.location_coordinates);

  return {
    name: job.location_name,
    address: job.location_address || '',
    lat: coordinates ? String(coordinates.lat) : '',
    lng: coordinates ? String(coordinates.lng) : '',
  };
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function DeliveryModal({
  isOpen,
  onClose,
  onSuccess,
  parentJob,
  editingDelivery,
  activeSolutionKey,
}: DeliveryModalProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';
  const isEditing = Boolean(editingDelivery);

  const { users: assignmentUsers, loading: loadingUsers, getTeamMemberUserIds } = useOperationsAssignmentOptions({ activeSolutionKey });
  const effectiveModuleContext = editingDelivery?.module_context || parentJob?.module_context || 'logistics-delivery';
  const deliverySubtypeOptions = useMemo(
    () => inferSubtypeOptions(effectiveModuleContext),
    [effectiveModuleContext],
  );

  // Filter users to only include team members when activeSolutionKey is provided
  const filteredAssignmentUsers = useMemo(() => {
    if (!activeSolutionKey) {
      return assignmentUsers;
    }
    const teamMemberUserIds = getTeamMemberUserIds();
    return assignmentUsers.filter((user) => teamMemberUserIds.includes(user.id));
  }, [assignmentUsers, activeSolutionKey, getTeamMemberUserIds]);

  // Determine if we're in waste-management-enterprise mode for skip integration
  const isWasteManagementEnterprise = activeSolutionKey === 'waste-management-enterprise' || effectiveModuleContext === 'waste-management';
  const parentContactId = normalizePositiveIntegerId(parentJob?.contact_id);

  // Load all available skips (assets) when in waste-management-enterprise mode
  const { items: allAvailableSkips, loading: loadingSkips, refreshItems: refreshSkips } = useWorkItemsData({
    itemType: 'asset',
    itemSubtype: 'skip',
    moduleContext: 'waste-management',
    autoLoad: isWasteManagementEnterprise && isOpen,
  });

  // Load customer skip allocations
  const [skipAllocations, setSkipAllocations] = useState<Array<{
    skip_type: string;
    skip_size_m3: number;
    allocated_quantity: number;
    contact_location_id: number | null;
  }>>([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);

  interface RawAllocationWorkItem {
    id: string | number;
    status: string;
    custom_fields?: {
      skip_type?: string;
      skip_size_m3?: number;
      allocated_quantity?: number;
      contact_location_id?: number | null;
    } | null;
  }

  // Fetch skip allocations when contact changes
  useEffect(() => {
    if (!isWasteManagementEnterprise || !isOpen || !parentContactId) {
      setSkipAllocations([]);
      return;
    }

    const fetchSkipAllocations = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        setSkipAllocations([]);
        return;
      }

      setLoadingAllocations(true);
      try {
        const response = await fetch(
          `/api/v1/work-items?item_type=reservation&item_subtype=skip_allocation&contact_id=${parentContactId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.ok) {
          const data = await response.json();
          const items = (data.data || data || []) as RawAllocationWorkItem[];
          // Transform work_items to allocation format
          const allocations = items
            .filter((item) => item.status !== 'cancelled')
            .map((item) => {
              const customFields = item.custom_fields || {};
              return {
                skip_type: customFields.skip_type || 'Standard',
                skip_size_m3: customFields.skip_size_m3 || 0,
                allocated_quantity: customFields.allocated_quantity || 1,
                contact_location_id: customFields.contact_location_id || null,
              };
            });
          setSkipAllocations(allocations);
        } else {
          setSkipAllocations([]);
        }
      } catch (error) {
        console.error('[DeliveryModal] Failed to load skip allocations:', error);
        setSkipAllocations([]);
      } finally {
        setLoadingAllocations(false);
      }
    };

    void fetchSkipAllocations();
  }, [isWasteManagementEnterprise, isOpen, parentContactId]);

  // Filter available skips based on customer allocations
  const availableSkips = useMemo(() => {
    if (!isWasteManagementEnterprise || !allAvailableSkips.length) {
      return allAvailableSkips;
    }

    // If no allocations, show all available skips (backward compatibility)
    if (!skipAllocations.length) {
      return allAvailableSkips.filter(skip => skip.status === 'available');
    }

    // Filter skips that match customer's allocated types/sizes and are available
    return allAvailableSkips.filter(skip => {
      if (skip.status !== 'available') {
        return false;
      }

      const skipCustomFields = skip.custom_fields || {};
      const skipType = skipCustomFields.skip_type || skipCustomFields.asset_type || 'Standard';
      const skipSize = Number(skipCustomFields.capacity || skipCustomFields.asset_capacity || 0);

      // Check if this skip type/size is allocated to the customer
      const hasAllocation = skipAllocations.some(allocation => {
        const typeMatch = allocation.skip_type.toLowerCase() === skipType.toLowerCase();
        const sizeMatch = Math.abs(allocation.skip_size_m3 - skipSize) < 0.1; // Allow small floating point differences
        return typeMatch && sizeMatch;
      });

      return hasAllocation;
    });
  }, [isWasteManagementEnterprise, allAvailableSkips, skipAllocations]);

  // Request ID-based state management for location loading
  const latestLocationLoadRequestId = useRef(0);
  const activeContactIdRef = useRef<number | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [itemSubtype, setItemSubtype] = useState(defaultSubtypeForModuleContext(effectiveModuleContext));
  const [status, setStatus] = useState('scheduled');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [locationMode, setLocationMode] = useState<DeliveryLocationMode>('inherited');
  const [contactLocations, setContactLocations] = useState<ContactLocation[]>([]);
  const [selectedContactLocationId, setSelectedContactLocationId] = useState<string>('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationLoadError, setLocationLoadError] = useState<string | null>(null);
  const [showLocationCreate, setShowLocationCreate] = useState(false);
  const [customLocation, setCustomLocation] = useState<CustomLocationData | null>(null);
  const [customLocSearch, setCustomLocSearch] = useState('');
  const [packageCount, setPackageCount] = useState('');
  const [totalWeightKg, setTotalWeightKg] = useState('');
  const [wasteClassification, setWasteClassification] = useState('');
  const [disposalSite, setDisposalSite] = useState('');
  const [selectedSkipId, setSelectedSkipId] = useState<string>('');
  const [priority, setPriority] = useState('medium');
  const [equipmentRequired, setEquipmentRequired] = useState('');
  const [safetyRequirements, setSafetyRequirements] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isWasteSubtype = itemSubtype.startsWith('waste_');
  const parentHasInheritedLocation = Boolean(parentJob?.location_name || parentJob?.location_address || parentJob?.location_id);
  const selectedContactLocation = useMemo(
    () => contactLocations.find((location) => String(location.id) === selectedContactLocationId) || null,
    [contactLocations, selectedContactLocationId],
  );
  const inheritedLocationSummary = useMemo(
    () => [parentJob?.location_name, parentJob?.location_address].filter((value): value is string => typeof value === 'string' && value.trim().length > 0).join(' — '),
    [parentJob?.location_address, parentJob?.location_name],
  );

  const selectedSubtypeDefinition = useMemo(
    () => deliverySubtypeOptions.find((option) => option.value === itemSubtype) || deliverySubtypeOptions[0],
    [deliverySubtypeOptions, itemSubtype],
  );

  const loadContactLocations = useCallback(async (contactId: number) => {
    const token = authService.getTokens()?.access_token;
    if (!token) {
      setContactLocations([]);
      setLocationLoadError('Please sign in to load customer locations.');
      return;
    }

    const requestId = ++latestLocationLoadRequestId.current;
    activeContactIdRef.current = contactId;
    setLoadingLocations(true);
    setLocationLoadError(null);

    try {
      const result = await requestContactLocations(contactId, token);

      if (requestId === latestLocationLoadRequestId.current && activeContactIdRef.current === contactId) {
        setContactLocations(result.locations);
        setLocationLoadError(result.errorMessage);
        setSelectedContactLocationId((current) => {
          if (current && result.locations.some((location) => String(location.id) === current)) {
            return current;
          }

          const parentMatch = result.locations.find((location) => matchesParentLocation(location, parentJob));
          const primaryLocation = result.locations.find((location) => location.is_primary);
          return parentMatch ? String(parentMatch.id) : primaryLocation ? String(primaryLocation.id) : '';
        });
      }
    } catch (error) {
      console.error('[DeliveryModal] Failed to load customer locations:', error);
      if (requestId === latestLocationLoadRequestId.current && activeContactIdRef.current === contactId) {
        setContactLocations([]);
        setLocationLoadError(error instanceof Error ? error.message : 'Failed to load customer locations');
      }
    } finally {
      if (requestId === latestLocationLoadRequestId.current && activeContactIdRef.current === contactId) {
        setLoadingLocations(false);
      }
    }
  }, [parentJob]);

  // Emit guided tour event when modal opens
  useEffect(() => {
    if (isOpen) {
      emitOperationsGuidedTourTrainingEvent({
        type: 'appointment_modal_opened',
        route: 'appointment_modal',
      });
    }
  }, [isOpen]);

  // Pre-populate from parent job or editing delivery
  useEffect(() => {
    if (!isOpen) return;

    const defaultSubtype = defaultSubtypeForModuleContext(effectiveModuleContext);

    if (editingDelivery) {
      const editingCustomFields = editingDelivery.custom_fields || {};
      setTitle(editingDelivery.title || '');
      setDescription(editingDelivery.description || '');
      setItemSubtype(editingDelivery.item_subtype || defaultSubtype);
      setStatus(editingDelivery.status || 'scheduled');
      setScheduledStart(editingDelivery.scheduled_start ? editingDelivery.scheduled_start.slice(0, 16) : '');
      setScheduledEnd(editingDelivery.scheduled_end ? editingDelivery.scheduled_end.slice(0, 16) : '');
      setAssignedTo(Array.isArray(editingDelivery.assigned_to) ? String(editingDelivery.assigned_to[0]) : String(editingDelivery.assigned_to || ''));
      setLocationName(editingDelivery.location_name || '');
      setLocationAddress(editingDelivery.location_address || '');
      setLocationMode(isEditingInheritedLocation(editingDelivery, parentJob)
        ? 'inherited'
        : normalizePositiveIntegerId(editingDelivery.location_id)
          ? 'contact'
          : 'custom');
      setSelectedContactLocationId(normalizePositiveIntegerId(editingDelivery.location_id) ? String(editingDelivery.location_id) : '');
      setPriority(normalizeStringValue(editingDelivery.priority || 'medium'));
      setEquipmentRequired(normalizeStringValue(editingCustomFields.equipment_required || ''));
      setSafetyRequirements(normalizeStringValue(editingCustomFields.safety_requirements || ''));
      setPackageCount(normalizeStringValue(editingCustomFields.number_of_packages || editingCustomFields.total_items));
      setTotalWeightKg(normalizeStringValue(editingCustomFields.total_weight_kg || editingCustomFields.net_weight_kg || editingCustomFields.estimated_weight_kg));
      setWasteClassification(normalizeStringValue(editingCustomFields.waste_classification || editingCustomFields.waste_type));
      setDisposalSite(normalizeStringValue(editingCustomFields.disposal_site || editingCustomFields.disposed_at));
      setSelectedSkipId(normalizeStringValue(editingCustomFields.skip_id || editingCustomFields.asset_id));
    } else if (parentJob) {
      const parentCustomFields = parentJob.custom_fields || {};
      setTitle(parentJob.title ? `${formatSubtypeLabel(defaultSubtype)} for ${parentJob.title}` : '');
      setDescription(parentJob.description || '');
      setItemSubtype(defaultSubtype);
      setStatus('scheduled');
      setScheduledStart('');
      setScheduledEnd('');
      setAssignedTo('');
      setLocationMode(parentHasInheritedLocation ? 'inherited' : parentContactId ? 'contact' : 'custom');
      setSelectedContactLocationId(normalizePositiveIntegerId(parentJob.location_id) ? String(parentJob.location_id) : '');
      setLocationName(parentJob.location_name || '');
      setLocationAddress(parentJob.location_address || '');
      setPriority('medium');
      setEquipmentRequired(normalizeStringValue(parentCustomFields.equipment_required || ''));
      setSafetyRequirements(normalizeStringValue(parentCustomFields.safety_requirements || ''));
      setPackageCount('');
      setTotalWeightKg(normalizeStringValue(parentCustomFields.total_weight_kg));
      setWasteClassification(normalizeStringValue(parentCustomFields.waste_classification || parentCustomFields.waste_type));
      setDisposalSite(normalizeStringValue(parentCustomFields.disposal_site));
      setSelectedSkipId('');
    } else {
      setItemSubtype(defaultSubtype);
      setLocationMode('custom');
      setSelectedContactLocationId('');
    }
    setContactLocations([]);
    setLocationLoadError(null);
    setErrors({});
    setShowLocationCreate(false);
    setCustomLocation(null);
    setCustomLocSearch('');
    latestLocationLoadRequestId.current += 1;
    activeContactIdRef.current = null;
  }, [isOpen, editingDelivery, parentJob, effectiveModuleContext, parentContactId, parentHasInheritedLocation]);

  useEffect(() => {
    if (!isOpen || !parentContactId || locationMode !== 'contact') {
      return;
    }

    void loadContactLocations(parentContactId);
  }, [isOpen, locationMode, loadContactLocations, parentContactId]);

  const validate = useCallback((): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!title.trim()) {
      nextErrors.title = 'Title is required';
    }
    if (locationMode === 'contact' && !selectedContactLocation) {
      nextErrors.location = 'Select a saved customer location';
    }
    if (locationMode === 'custom' && !customLocation && !locationName.trim()) {
      nextErrors.location = 'Provide a custom location';
    }
    if (locationMode === 'inherited' && !parentHasInheritedLocation) {
      nextErrors.location = 'This job does not have a stored customer location yet — choose an override';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [title, locationMode, selectedContactLocation, locationName, parentHasInheritedLocation, customLocation]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;

    const tokens = authService.getTokens();
    if (!tokens) {
      notify.error('Please log in');
      return;
    }

    setSaving(true);
    try {
      const parsedPackageCount = packageCount.trim() ? Number(packageCount) : null;
      const parsedTotalWeightKg = totalWeightKg.trim() ? Number(totalWeightKg) : null;
      const resolvedLocationCoordinates = locationMode === 'contact'
        ? getContactLocationCoordinates(selectedContactLocation)
        : locationMode === 'inherited'
          ? extractCoordinates(parentJob?.location_coordinates)
          : customLocation
            ? { lat: Number(customLocation.lat), lng: Number(customLocation.lng) }
            : null;

      const resolvedLocationName = locationMode === 'contact'
        ? selectedContactLocation?.location_name || null
        : locationMode === 'inherited'
          ? parentJob?.location_name || null
          : customLocation?.name || locationName.trim() || null;

      const resolvedLocationAddress = locationMode === 'contact'
        ? buildLocationDisplayLabel(selectedContactLocation) || null
        : locationMode === 'inherited'
          ? parentJob?.location_address || null
          : customLocation?.address || locationAddress.trim() || null;

      const customFields = {
        ...(editingDelivery?.custom_fields || {}),
        number_of_packages: Number.isFinite(parsedPackageCount) ? parsedPackageCount : null,
        total_items: Number.isFinite(parsedPackageCount) ? parsedPackageCount : null,
        total_weight_kg: Number.isFinite(parsedTotalWeightKg) ? parsedTotalWeightKg : null,
        waste_classification: wasteClassification.trim() || null,
        disposal_site: disposalSite.trim() || null,
        skip_id: selectedSkipId.trim() || null,
        location_mode: locationMode,
        equipment_required: equipmentRequired.trim() || null,
        safety_requirements: safetyRequirements.trim() || null,
      };

      const body: Record<string, unknown> = {
        title: title.trim(),
        description: description.trim() || null,
        item_type: 'delivery',
        item_subtype: itemSubtype,
        status,
        priority,
        scheduled_start: scheduledStart || null,
        scheduled_end: scheduledEnd || null,
        assigned_to: assignedTo ? Number(assignedTo) : null,
        location_id: locationMode === 'contact' ? selectedContactLocation?.id || null : locationMode === 'inherited' ? parentJob?.location_id || null : null,
        location_name: resolvedLocationName,
        location_address: resolvedLocationAddress,
        ...(resolvedLocationCoordinates ? { location_coordinates: resolvedLocationCoordinates } : {}),
        module_context: effectiveModuleContext,
        custom_fields: customFields,
      };

      if (parentJob?.contact_id) {
        body.contact_id = parentJob.contact_id;
        body.contact_name = parentJob.contact_name;
      }

      const url = isEditing
        ? `/api/v1/work-items/${editingDelivery!.id}`
        : '/api/v1/work-items';

      const res = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        notify.error(data.error || `Failed to ${isEditing ? 'update' : 'create'} delivery`);
        return;
      }

      const deliveryId = isEditing ? editingDelivery!.id : (data.data?.id || data.id);

      // Link to parent job via relationship
      if (!isEditing && deliveryId && parentJob?.id) {
        try {
          await fetch('/api/v1/work-item-relationships', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokens.access_token}`,
            },
            body: JSON.stringify({
              action: 'create',
              sourceItemId: deliveryId,
              targetItemId: parentJob.id,
              relationshipType: 'child_of',
            }),
          });
        } catch (relError) {
          console.warn('[DeliveryModal] Failed to link delivery to job:', relError);
        }
      }

      notify.success(`Delivery ${isEditing ? 'updated' : 'created'} successfully`);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('[DeliveryModal] Save error:', error);
      notify.error(`Failed to ${isEditing ? 'update' : 'create'} delivery`);
    } finally {
      setSaving(false);
    }
  }, [validate, title, description, itemSubtype, status, priority, scheduledStart, scheduledEnd, assignedTo, locationMode, selectedContactLocation, locationName, locationAddress, customLocation, packageCount, totalWeightKg, wasteClassification, disposalSite, selectedSkipId, equipmentRequired, safetyRequirements, isEditing, editingDelivery, parentJob, effectiveModuleContext, onSuccess, onClose]);

  const assigneeOptions = useMemo(() => {
    return filteredAssignmentUsers.map((u) => ({
      value: String(u.id),
      label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User ${u.id}`,
    }));
  }, [filteredAssignmentUsers]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-gray-50 dark:bg-gray-900 overflow-y-auto" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <div className="px-2 pb-1 pt-2 sm:px-4 sm:pb-3 sm:pt-4">
        <div className="mb-3 sm:mb-6 px-2 relative">
          <div className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg sm:mb-3 sm:h-16 sm:w-16">
              <Truck className="h-6 w-6 sm:h-8 sm:w-8" />
            </div>
            <h1 className="pm-heading-1 text-lg sm:text-2xl mb-1 text-gray-900 dark:text-white">{isEditing ? 'Edit Delivery' : 'New Delivery'}</h1>
            {parentJob?.title && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                Parent: {parentJob.title}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="absolute right-2 top-0 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-400 transition-colors hover:border-gray-300 hover:text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-500 dark:hover:border-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-w-5xl mx-auto p-6 space-y-4">
          <div className={`rounded-lg border px-3 py-3 ${isDark ? 'border-gray-700 bg-gray-800/60 text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
            <div className="flex items-start gap-3">
              <Truck className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <div>
                <p className="text-sm font-medium">
                  {selectedSubtypeDefinition?.label || 'Delivery workflow'}
                </p>
                <p className="mt-1 text-xs">
                  {selectedSubtypeDefinition?.description || 'Create a logistics child item linked to this job.'}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Delivery Type
            </label>
            <select
              value={itemSubtype}
              onChange={(e) => setItemSubtype(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
            >
              {deliverySubtypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Title */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Delivery to Site A"
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'} ${errors.title ? 'border-red-500' : ''}`}
            />
            {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional delivery instructions..."
              rows={3}
              className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
            />
          </div>

          {/* Status & Assignment row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
              >
                {DELIVERY_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  Assigned To
                </span>
              </label>
              <select
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                disabled={loadingUsers}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
              >
                <option value="">Unassigned</option>
                {assigneeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Schedule row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  Scheduled Start
                </span>
              </label>
              <input
                type="datetime-local"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Scheduled End
                </span>
              </label>
              <input
                type="datetime-local"
                value={scheduledEnd}
                onChange={(e) => setScheduledEnd(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-3">
            <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                Delivery Location
              </span>
            </label>

            <div className={`rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-start gap-3">
                <Building2 className={`mt-0.5 h-4 w-4 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {locationMode === 'inherited'
                      ? 'Using the job / customer location'
                      : locationMode === 'contact'
                        ? 'Overriding with another saved customer location'
                        : 'Using a custom delivery location'}
                  </p>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {locationMode === 'inherited'
                      ? (inheritedLocationSummary || 'No stored location is available on this job yet.')
                      : locationMode === 'contact'
                        ? (selectedContactLocation ? buildLocationDisplayLabel(selectedContactLocation) : 'Choose a saved location below.')
                        : (customLocation?.name || locationName || locationAddress || 'Provide a custom location below.')}
                  </p>
                </div>
              </div>
            </div>

            <div className={`flex gap-1 rounded-lg p-1 ${isDark ? 'bg-gray-800/70' : 'bg-gray-100'}`}>
              <button
                type="button"
                onClick={() => setLocationMode('inherited')}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${locationMode === 'inherited'
                  ? (isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow-sm')
                  : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')}`}
              >
                Use job location
              </button>
              {parentContactId ? (
                <button
                  type="button"
                  onClick={() => setLocationMode('contact')}
                  className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${locationMode === 'contact'
                    ? (isDark ? 'bg-blue-700 text-white' : 'bg-blue-600 text-white shadow-sm')
                    : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')}`}
                >
                  Saved customer location
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setLocationMode('custom')}
                className={`flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors ${locationMode === 'custom'
                  ? (isDark ? 'bg-orange-700 text-white' : 'bg-orange-500 text-white shadow-sm')
                  : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800')}`}
              >
                Custom override
              </button>
            </div>

            {locationMode === 'contact' && (
              <div className="space-y-2">
                {loadingLocations ? (
                  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading customer locations…
                  </div>
                ) : locationLoadError ? (
                  <div className={`rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-red-700 bg-red-900/20 text-red-200' : 'border-red-200 bg-red-50 text-red-700'}`}>
                    {locationLoadError}
                  </div>
                ) : contactLocations.length > 0 ? (
                  <select
                    value={selectedContactLocationId}
                    onChange={(e) => setSelectedContactLocationId(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                  >
                    <option value="">Select saved location…</option>
                    {contactLocations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.location_name}{location.is_primary ? ' ★' : ''}{buildLocationDisplayLabel(location) ? ` — ${buildLocationDisplayLabel(location)}` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className={`rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                    No saved customer locations are available. Use a custom override instead.
                  </div>
                )}
              </div>
            )}

            {locationMode === 'custom' && (
              <div className="space-y-3">
                <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${isDark ? 'border-orange-800/50 bg-orange-900/20 text-orange-300' : 'border-orange-200 bg-orange-50 text-orange-700'}`}>
                  <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>This custom location only affects this delivery stop and does not overwrite the customer&apos;s saved locations.</span>
                </div>
                <LocationAutocomplete
                  value={customLocSearch}
                  onChange={setCustomLocSearch}
                  onPlaceSelect={(data) => setCustomLocation(data)}
                  onClear={() => {
                    setCustomLocation(null);
                    setCustomLocSearch('');
                  }}
                  placeholder="Search for a location or enter a custom address"
                  isDark={isDark}
                />
                {showLocationCreate && (
                  <QuickLocationForm
                    contactId={parentContactId || 0}
                    onCreated={(createdLocation: CreatedLocation) => {
                      setCustomLocation({
                        name: createdLocation.location_name,
                        address: createdLocation.address_line1 || '',
                        lat: createdLocation.latitude ? String(createdLocation.latitude) : '',
                        lng: createdLocation.longitude ? String(createdLocation.longitude) : '',
                      });
                      setCustomLocSearch(createdLocation.location_name);
                      setShowLocationCreate(false);
                    }}
                    onCancel={() => setShowLocationCreate(false)}
                  />
                )}
              </div>
            )}

            {errors.location && <p className="text-xs text-red-500">{errors.location}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Target className="h-3.5 w-3.5" />
                  Priority
                </span>
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Package className="h-3.5 w-3.5" />
                  Packages / Items
                </span>
              </label>
              <input
                type="number"
                min="0"
                value={packageCount}
                onChange={(e) => setPackageCount(e.target.value)}
                placeholder="Total packages or items"
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Scale className="h-3.5 w-3.5" />
                  Total Weight (kg)
                </span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={totalWeightKg}
                onChange={(e) => setTotalWeightKg(e.target.value)}
                placeholder="Load or waste weight"
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <Briefcase className="h-3.5 w-3.5" />
                  Equipment Required
                </span>
              </label>
              <input
                type="text"
                value={equipmentRequired}
                onChange={(e) => setEquipmentRequired(e.target.value)}
                placeholder="Forklift, pallet jack, tools..."
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                <span className="flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Safety Requirements
                </span>
              </label>
              <input
                type="text"
                value={safetyRequirements}
                onChange={(e) => setSafetyRequirements(e.target.value)}
                placeholder="PPE, harness, safety barriers..."
                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
              />
            </div>

            {isWasteSubtype && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Waste Classification
                </label>
                <input
                  type="text"
                  value={wasteClassification}
                  onChange={(e) => setWasteClassification(e.target.value)}
                  placeholder="General, recyclable, hazardous..."
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
                />
              </div>
            )}

            {isWasteSubtype && (
              <div className="sm:col-span-2">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="flex items-center gap-1">
                    <Factory className="h-3.5 w-3.5" />
                    Disposal / Receiving Site
                  </span>
                </label>
                <input
                  type="text"
                  value={disposalSite}
                  onChange={(e) => setDisposalSite(e.target.value)}
                  placeholder="Enviroserv, transfer station, receiving site..."
                  className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100 placeholder:text-gray-500' : 'border-gray-300 bg-white text-gray-900 placeholder:text-gray-400'}`}
                />
              </div>
            )}

            {isWasteManagementEnterprise && (
              <div className="sm:col-span-2">
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="flex items-center gap-1">
                    <Package className="h-3.5 w-3.5" />
                    Skip (Optional)
                  </span>
                </label>
                {loadingSkips || loadingAllocations ? (
                  <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{loadingAllocations ? 'Loading skip allocations…' : 'Loading skips…'}</span>
                  </div>
                ) : availableSkips.length > 0 ? (
                  <select
                    value={selectedSkipId}
                    onChange={(e) => setSelectedSkipId(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDark ? 'border-gray-700 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-900'}`}
                  >
                    <option value="">Select a skip (optional)</option>
                    {availableSkips.map((skip) => {
                      const skipCustomFields = skip.custom_fields || {};
                      const skipId = skipCustomFields.skip_id || skipCustomFields.asset_id || skip.item_number || skip.id;
                      const skipType = skipCustomFields.skip_type || skipCustomFields.asset_type || 'Standard';
                      const capacity = skipCustomFields.capacity || skipCustomFields.asset_capacity || '';
                      return (
                        <option key={skip.id} value={skip.id}>
                          {skipId} — {skipType}{capacity ? ` (${capacity}m³)` : ''} — {skip.title}
                        </option>
                      );
                    })}
                  </select>
                ) : (
                  <div className={`space-y-2`}>
                    <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${isDark ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-300 bg-white text-gray-600'}`}>
                      <Info className="h-4 w-4" />
                      <span>
                        {skipAllocations.length === 0 
                          ? 'No skip allocations configured for this customer. Contact admin to allocate skips.'
                          : 'No matching empty skips available. All allocated skips are currently deployed.'}
                      </span>
                    </div>
                    {skipAllocations.length > 0 && (
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Allocated: {skipAllocations.map(a => `${a.skip_type} (${a.skip_size_m3}m³)`).join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t max-w-5xl mx-auto ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                {isEditing ? 'Update Delivery' : 'Create Delivery'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
